import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../utils/config';
import { apiFetch } from '../utils/apiFetch';
import { getPrescriptionFile, clearPrescriptionFiles } from '../utils/prescriptionStore';
import { getLensSelections, clearLensSelections, LensData } from '../utils/lensStore';
import { uploadPrescription } from '../api/prescription';

interface Address {
  id: string;
  name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  mobile: string;
  country?: string;
  is_primary?: boolean;
  deleted?: boolean;
}

type PaymentMethod = 'card' | 'cod';

declare global {
  interface Window {
    Razorpay?: any;
  }
}

const loadRazorpayScript = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    if (document.getElementById('razorpay-script')) { resolve(true); return; }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const EMPTY_FORM = { name: '', address_line1: '', address_line2: '', city: '', state: '', postal_code: '', mobile: '', country: 'india' };

const fmtLabel = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());

const buildLensName = (lens: LensData): string => {
  if (!lens.lensType) return 'Lens';
  const base = fmtLabel(lens.lensType);
  return lens.coating && lens.coating !== 'normal'
    ? `${base} Lens with ${fmtLabel(lens.coating)} Coating`
    : `${base} Lens`;
};

const SKIP_LENS_CATEGORIES = [3, 4, 5];

const AddressPage: React.FC = () => {
  const { cartItems, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cod');
  // null = still checking; true/false = whether this store has Razorpay configured
  const [razorpayEnabled, setRazorpayEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Per-item lens selections (read once on mount)
  const [lensSelections] = useState<Record<string, LensData>>(() => getLensSelections());

  const frameItems = cartItems.filter(
    (item) => !SKIP_LENS_CATEGORIES.includes(item.category_id ?? 0)
  );
  const totalLensPrice = frameItems.reduce((sum, item) => {
    const lens = lensSelections[item.id];
    return sum + (lens && lens.lensType !== 'frame_only' ? (lens.coatingPrice ?? 0) : 0);
  }, 0);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    loadAddresses();
    loadRazorpayConfig();
  }, []);

  const loadRazorpayConfig = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/razorpay/config`);
      if (res.ok) {
        const data = await res.json();
        const enabled = !!data.enabled;
        setRazorpayEnabled(enabled);
        // Default to the online card option only when it's available
        if (enabled) setPaymentMethod('card');
      } else {
        setRazorpayEnabled(false);
      }
    } catch {
      setRazorpayEnabled(false);
    }
  };

  const loadAddresses = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/customers/${user!.userId}`);
      if (res.ok) {
        const data = await res.json();
        const addrs: Address[] = (data.customer?.addresses || []).map((a: any) => ({
          id: a.id,
          name: a.name,
          address_line1: a.address_line1,
          address_line2: a.address_line2 || '',
          city: a.city,
          state: a.state,
          postal_code: a.postal_code,
          mobile: a.mobile,
          country: a.country || 'india',
          is_primary: a.is_primary || false,
        }));
        setAddresses(addrs);
        if (addrs.length > 0) setSelectedAddress((prev) => prev ?? addrs[0]);
      }
    } catch {}
    setLoading(false);
  };

  const validateForm = () => {
    const errs: Record<string, string> = {};
    if (!form.name || form.name.length < 2) errs.name = 'Name required (min 2 chars)';
    if (!form.address_line1 || form.address_line1.length < 5) errs.address_line1 = 'Address required (min 5 chars)';
    if (!form.city || form.city.length < 2) errs.city = 'City required';
    if (!form.state || form.state.length < 2) errs.state = 'State required';
    if (!form.postal_code || !/^\d{5,6}$/.test(form.postal_code)) errs.postal_code = 'Postal code: 5–6 digits';
    if (!form.mobile || !/^\d{10}$/.test(form.mobile)) errs.mobile = 'Mobile: 10 digits';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const openAddModal = () => {
    setIsEditing(false); setEditId(null); setForm({ ...EMPTY_FORM }); setFormErrors({}); setModalOpen(true);
  };

  const openEditModal = (addr: Address) => {
    setIsEditing(true); setEditId(addr.id);
    setForm({ name: addr.name, address_line1: addr.address_line1, address_line2: addr.address_line2 || '', city: addr.city, state: addr.state, postal_code: addr.postal_code, mobile: addr.mobile, country: addr.country || 'india' });
    setFormErrors({}); setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditId(null); setIsEditing(false); setForm({ ...EMPTY_FORM }); };

  const handleSaveAddress = async () => {
    if (!validateForm()) return;
    setSavingAddress(true);
    try {
      const updatedAddresses = isEditing && editId
        ? addresses.map((a) => (a.id === editId ? { ...a, ...form } : a))
        : [...addresses, { id: '', ...form }];
      const res = await apiFetch(`${API_BASE}/customers/${user!.userId}`, {
        method: 'PUT',
        body: JSON.stringify({ addresses: updatedAddresses }),
      });
      if (res.ok) { await loadAddresses(); closeModal(); }
      else { alert('Failed to save address. Please try again.'); }
    } catch {
      alert('Failed to save address. Please try again.');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleDeleteAddress = async () => {
    if (!deleteId) return;
    const res = await apiFetch(`${API_BASE}/customers/${user!.userId}`, {
      method: 'PUT',
      body: JSON.stringify({ addresses: [{ id: deleteId, deleted: true }] }),
    });
    if (res.ok) {
      if (selectedAddress?.id === deleteId) setSelectedAddress(null);
      await loadAddresses();
    } else { alert('Failed to delete address.'); }
    setDeleteModalOpen(false);
    setDeleteId(null);
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) { alert('Please select a delivery address.'); return; }
    if (cartItems.length === 0) { alert('Your cart is empty.'); return; }

    setProcessing(true);
    setError('');
    try {
      if (paymentMethod === 'card') {
        // Upload prescriptions before opening Razorpay (same as COD)
        const prescriptionUrls: Record<string, string | null> = {};
        for (const item of frameItems) {
          if (lensSelections[item.id]?.hasPrescription) {
            const file = getPrescriptionFile(item.id);
            if (file) {
              prescriptionUrls[item.id] = await uploadPrescription(file, String(user!.userId));
            }
          }
        }

        // Build allItems with pair_id + lens line items — same logic as COD
        const allItems: any[] = [];
        let pairCounter = 1;
        for (const item of cartItems) {
          const isFrame = !SKIP_LENS_CATEGORIES.includes(item.category_id ?? 0);
          const lens = isFrame ? lensSelections[item.id] : null;
          const hasRealLens = lens?.lensType && lens.lensType !== 'frame_only';
          const pairId = hasRealLens ? pairCounter : null;
          allItems.push({ ...item, pair_id: pairId });
          if (hasRealLens) {
            allItems.push({
              id: null,
              product_id: null,
              product_name: buildLensName(lens!),
              selling_price: lens!.coatingPrice ?? 0,
              quantity: 1,
              image_uri: [`/${lens!.lensType}.png`],
              lensSelectionData: lens,
              prescription_url: prescriptionUrls[item.id] ?? null,
              pair_id: pairId,
            });
            pairCounter++;
          }
        }

        const totalPayable = Math.round(cartTotal + totalLensPrice);

        // 1. Load Razorpay checkout script
        const loaded = await loadRazorpayScript();
        if (!loaded) throw new Error('Failed to load Razorpay. Check your internet connection.');

        // 2. Create an order on the backend
        const orderRes = await apiFetch(`${API_BASE}/razorpay/create-order`, {
          method: 'POST',
          body: JSON.stringify({ amount: totalPayable, currency: 'INR' }),
        });
        if (!orderRes.ok) {
          const err = await orderRes.json().catch(() => ({}));
          throw new Error(err.detail || 'Could not create payment order.');
        }
        const order = await orderRes.json();

        // 3. Open the Razorpay checkout in-page
        const options = {
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: 'Aadhithya Eyewear',
          description: 'Order payment',
          order_id: order.order_id,
          prefill: {
            name: selectedAddress?.name || '',
            contact: selectedAddress?.mobile || '',
          },
          notes: { address_id: selectedAddress?.id || '' },
          theme: { color: '#2563eb' },
          handler: async (response: {
            razorpay_payment_id: string;
            razorpay_order_id: string;
            razorpay_signature: string;
          }) => {
            try {
              const verifyRes = await apiFetch(`${API_BASE}/razorpay/verify-payment`, {
                method: 'POST',
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                  customer_id: user!.userId,
                  items_cart: allItems,
                }),
              });
              if (!verifyRes.ok) {
                const err = await verifyRes.json().catch(() => ({}));
                throw new Error(err.detail || 'Payment verification failed.');
              }
              // clearCart() already removes each item from the backend cart.
              clearPrescriptionFiles();
              clearLensSelections();
              await clearCart();
              setOrderSuccess(true);
            } catch (verifyErr: any) {
              setError(verifyErr.message || 'Payment verification failed.');
            } finally {
              setProcessing(false);
            }
          },
          modal: {
            ondismiss: () => setProcessing(false),
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (response: any) => {
          apiFetch(`${API_BASE}/razorpay/payment-failed`, {
            method: 'POST',
            body: JSON.stringify({
              order_id: order.order_id,
              reason: response.error?.description,
            }),
          }).catch(() => {});
          setError(response.error?.description || 'Payment failed. Please try again.');
          setProcessing(false);
        });
        rzp.open();
        // Razorpay drives the rest via callbacks; stop the outer spinner.
        return;
      } else {
        const today = new Date().toISOString().split('T')[0];

        // Upload prescriptions per frame item that has one
        const prescriptionUrls: Record<string, string | null> = {};
        for (const item of frameItems) {
          if (lensSelections[item.id]?.hasPrescription) {
            const file = getPrescriptionFile(item.id);
            if (file) {
              prescriptionUrls[item.id] = await uploadPrescription(file, String(user!.userId));
            }
          }
        }

        // Build order items: regular cart items + one lens line item per frame with selection.
        // Frame + its lens share the same pair_id so the main app can group them as a set.
        const allItems: any[] = [];
        let pairCounter = 1;
        for (const item of cartItems) {
          const isFrame = !SKIP_LENS_CATEGORIES.includes(item.category_id ?? 0);
          const lens = isFrame ? lensSelections[item.id] : null;
          const hasRealLens = lens?.lensType && lens.lensType !== 'frame_only';
          const pairId = hasRealLens ? pairCounter : null;
          allItems.push({ ...item, pair_id: pairId });
          if (hasRealLens) {
            allItems.push({
              id: null,
              product_id: null,
              product_name: buildLensName(lens!),
              selling_price: lens!.coatingPrice ?? 0,
              quantity: 1,
              image_uri: [`/${lens!.lensType}.png`],
              lensSelectionData: lens,
              prescription_url: prescriptionUrls[item.id] ?? null,
              pair_id: pairId,
            });
            pairCounter++;
          }
        }

        const totalPayable = cartTotal + totalLensPrice;

        const res = await apiFetch(`${API_BASE}/order`, {
          method: 'POST',
          body: JSON.stringify({
            amount: totalPayable,
            amount_paid: 0,
            cartItems: allItems,
            billing_date: today,
            delivery_date: today,
            billed_by: 'customer',
            customer_id: user!.userId,
            is_gst_enabled: false,
            remark: 'Order from frontend',
            bill_to_name: 'Auto Generated',
            product_id: null,
          }),
        });
        if (res.ok) {
          clearPrescriptionFiles();
          clearLensSelections();
          await clearCart();
          setOrderSuccess(true);
        } else {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'Order placement failed');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (!user) return null;

  return (
    <div className="checkout-page">
      <h1 className="checkout-page__title">Delivery &amp; Payment</h1>

      <div className="checkout-layout">
        {/* Left: Address + Payment */}
        <div className="checkout-forms">
          {/* Delivery Address */}
          <div className="checkout-section">
            <div className="addr-section-header">
              <h3 style={{ margin: 0 }}>Delivery Address</h3>
              <button className="btn btn--sm btn--outline" onClick={openAddModal}>+ Add Address</button>
            </div>

            {loading ? (
              <p className="addr-empty">Loading addresses...</p>
            ) : addresses.length === 0 ? (
              <p className="addr-empty">No saved addresses. Please add one to continue.</p>
            ) : (
              <div className="addr-list">
                {addresses.map((addr) => (
                  <div
                    key={addr.id}
                    className={`addr-card ${selectedAddress?.id === addr.id ? 'addr-card--selected' : ''}`}
                    onClick={() => setSelectedAddress(addr)}
                  >
                    <div className="addr-card__info">
                      <p className="addr-card__name">{addr.name}</p>
                      <p className="addr-card__line">{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}</p>
                      <p className="addr-card__line">{addr.city}, {addr.state} – {addr.postal_code}</p>
                      <p className="addr-card__line">Mobile: {addr.mobile}</p>
                    </div>
                    <div className="addr-card__actions">
                      <button
                        className={`addr-select-btn ${selectedAddress?.id === addr.id ? 'addr-select-btn--active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setSelectedAddress(selectedAddress?.id === addr.id ? null : addr); }}
                      >
                        {selectedAddress?.id === addr.id ? 'Unselect' : 'Select'}
                      </button>
                      <button className="addr-icon-btn" onClick={(e) => { e.stopPropagation(); openEditModal(addr); }} title="Edit">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                      </button>
                      <button className="addr-icon-btn addr-icon-btn--danger" onClick={(e) => { e.stopPropagation(); setDeleteId(addr.id); setDeleteModalOpen(true); }} title="Delete">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div className="checkout-section">
            <h3>Payment Method</h3>
            <div className="payment-methods">
              {razorpayEnabled && (
                <label className={`payment-option ${paymentMethod === 'card' ? 'payment-option--selected' : ''}`}>
                  <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} />
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                    <rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                  <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                    <span style={{ fontWeight: 600 }}>Pay Online</span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>UPI, Cards, Net Banking &amp; Wallets</span>
                  </span>
                </label>
              )}
              <label className={`payment-option ${paymentMethod === 'cod' ? 'payment-option--selected' : ''}`}>
                <input type="radio" name="payment" value="cod" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} />
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <rect x="1" y="3" width="15" height="13" rx="1" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
                <span>Cash on Delivery</span>
              </label>
            </div>
            {paymentMethod === 'cod' && (
              <p style={{ marginTop: 10, fontSize: 13, color: '#6b7280', padding: '10px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                Pay with cash when your order is delivered. Our delivery partner will collect the amount.
              </p>
            )}
          </div>

          {error && <div className="modal-error">{error}</div>}
        </div>

        {/* Right: Order Summary */}
        <div className="order-summary checkout-summary">
          <h3 className="order-summary__title">Order Summary</h3>

          <div className="checkout-items">
            {cartItems.map((item) => {
              const lens = lensSelections[item.id];
              return (
                <React.Fragment key={item.id}>
                  <div className="checkout-item">
                    <img src={item.image_uri?.[0] || ''} alt={item.product_name} className="checkout-item__img" />
                    <div className="checkout-item__info">
                      <p className="checkout-item__name">{item.product_name}</p>
                      <p className="checkout-item__qty">x{item.quantity}</p>
                    </div>
                    <span className="checkout-item__price">
                      ₹{Math.round(item.selling_price * item.quantity).toLocaleString()}
                    </span>
                  </div>
                  {lens?.lensType && lens.lensType !== 'frame_only' && (
                    <div className="checkout-item checkout-item--lens">
                      <img src={`/${lens.lensType}.png`} alt="lens" className="checkout-item__img" style={{ objectFit: 'cover' }} />
                      <div className="checkout-item__info">
                        <p className="checkout-item__name">{buildLensName(lens)}</p>
                        <p className="checkout-item__qty">x1</p>
                      </div>
                      <span className="checkout-item__price">
                        {lens.coatingPrice > 0 ? `₹${lens.coatingPrice.toLocaleString()}` : 'Free'}
                      </span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="order-summary__rows">
            <div className="order-summary__row">
              <span>Subtotal</span>
              <span>₹{Math.round(cartTotal + totalLensPrice).toLocaleString()}</span>
            </div>
            <div className="order-summary__row">
              <span>Delivery</span>
              <span style={{ color: '#16a34a' }}>Free</span>
            </div>
          </div>

          <div className="order-summary__total">
            <span>Total Payable</span>
            <span>₹{Math.round(cartTotal + totalLensPrice).toLocaleString()}</span>
          </div>

          <button
            className="btn btn--primary order-summary__checkout-btn"
            onClick={handlePlaceOrder}
            disabled={processing || cartItems.length === 0 || !selectedAddress}
          >
            {processing ? 'Processing...' : paymentMethod === 'cod' ? 'Place Order' : 'Pay Now'}
          </button>
        </div>
      </div>

      {/* Add / Edit Address Modal */}
      {modalOpen && (
        <div className="addr-modal-overlay" onClick={closeModal}>
          <div className="addr-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="addr-modal__close"
              onClick={closeModal}
              disabled={savingAddress}
              aria-label="Close"
              title="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <h3 className="addr-modal__title">{isEditing ? 'Edit Address' : 'Add New Address'}</h3>
            <div className="addr-form">
              <div className="addr-form__field">
                <label>Name</label>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full Name" className={formErrors.name ? 'addr-input--error' : ''} />
                {formErrors.name && <span className="addr-form__error">{formErrors.name}</span>}
              </div>
              <div className="addr-form__field">
                <label>Address Line 1</label>
                <input value={form.address_line1} onChange={(e) => setForm((f) => ({ ...f, address_line1: e.target.value }))} placeholder="Street, Building, No." className={formErrors.address_line1 ? 'addr-input--error' : ''} />
                {formErrors.address_line1 && <span className="addr-form__error">{formErrors.address_line1}</span>}
              </div>
              <div className="addr-form__field">
                <label>Address Line 2 (Optional)</label>
                <input value={form.address_line2} onChange={(e) => setForm((f) => ({ ...f, address_line2: e.target.value }))} placeholder="Landmark, Area" />
              </div>
              <div className="addr-form__row">
                <div className="addr-form__field">
                  <label>City</label>
                  <input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder="City" className={formErrors.city ? 'addr-input--error' : ''} />
                  {formErrors.city && <span className="addr-form__error">{formErrors.city}</span>}
                </div>
                <div className="addr-form__field">
                  <label>State</label>
                  <input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} placeholder="State" className={formErrors.state ? 'addr-input--error' : ''} />
                  {formErrors.state && <span className="addr-form__error">{formErrors.state}</span>}
                </div>
              </div>
              <div className="addr-form__row">
                <div className="addr-form__field">
                  <label>Postal Code</label>
                  <input value={form.postal_code} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))} placeholder="6-digit PIN" className={formErrors.postal_code ? 'addr-input--error' : ''} />
                  {formErrors.postal_code && <span className="addr-form__error">{formErrors.postal_code}</span>}
                </div>
                <div className="addr-form__field">
                  <label>Mobile</label>
                  <input value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} placeholder="10-digit number" className={formErrors.mobile ? 'addr-input--error' : ''} />
                  {formErrors.mobile && <span className="addr-form__error">{formErrors.mobile}</span>}
                </div>
              </div>
            </div>
            <div className="addr-modal__actions">
              <button className="btn btn--outline" onClick={closeModal} disabled={savingAddress}>Cancel</button>
              <button className="btn btn--primary" onClick={handleSaveAddress} disabled={savingAddress}>
                {savingAddress ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span className="addr-spinner" />
                    {isEditing ? 'Updating...' : 'Adding...'}
                  </span>
                ) : (
                  isEditing ? 'Update' : 'Add Address'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteModalOpen && (
        <div className="addr-modal-overlay" onClick={() => setDeleteModalOpen(false)}>
          <div className="addr-modal addr-modal--sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="addr-modal__title">Delete Address</h3>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>Are you sure you want to delete this address?</p>
            <div className="addr-modal__actions">
              <button className="btn btn--outline" onClick={() => setDeleteModalOpen(false)}>Cancel</button>
              <button className="btn btn--danger" onClick={handleDeleteAddress}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Order Success Modal */}
      {orderSuccess && (
        <div className="addr-modal-overlay">
          <div className="addr-modal addr-modal--sm">
            <div className="addr-success-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
            </div>
            <h3 className="addr-modal__title" style={{ textAlign: 'center' }}>Order Confirmed!</h3>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24, textAlign: 'center' }}>
              We're getting your order ready. You can track its status below.
            </p>
            <div className="addr-modal__actions">
              <button className="btn btn--outline" onClick={() => { setOrderSuccess(false); navigate('/'); }}>OK</button>
              <button className="btn btn--primary" onClick={() => { setOrderSuccess(false); navigate('/orders'); }}>Track Order</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressPage;
